import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, asset, formatApiError } from "@/lib/api";
import { useList } from "@/hooks/useList";
import { ConfirmDelete } from "./AdminCollection";
import { FileField } from "./FileField";
import { PagesField } from "./PagesField";
import { field, Label, FormActions } from "./formBits";

const SeriesForm = ({ initial, onSave, onCancel, onChanged }) => {
  const [f, setF] = useState({ title: "", description: "", cover_url: "", order: 0, ...initial });
  const [pages, setPages] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await onSave({ title: f.title, description: f.description, cover_url: f.cover_url, order: Number(f.order) || 0 }, pages);
    setBusy(false);
  };
  return (
    <div className="space-y-6">
    <form onSubmit={submit} data-testid="series-form" className="space-y-5">
      <label className="block"><Label>SERIES TITLE</Label><input data-testid="series-title-input" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={field} /></label>
      <label className="block"><Label>DESCRIPTION</Label><textarea data-testid="series-description-input" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={field} /></label>
      <FileField label="COVER IMAGE — THUMBNAIL ONLY (TALL WORKS BEST)" kind="image" accept="image/*" value={f.cover_url} onChange={(v) => setF({ ...f, cover_url: v })} testid="series-cover" />
      <p className="text-xs text-zinc-400 -mt-2">The cover only appears on the series card. The comic itself is made of episode panels{initial?.id ? " — manage them below." : " — upload Episode 1 below."}</p>
      {!initial?.id && (
        <div className="border border-[#F5A524]/30 p-4">
          <p className="font-display font-black uppercase text-xl mb-3">Episode 01 panels</p>
          <PagesField pages={pages} onChange={setPages} />
        </div>
      )}
      <label className="block"><Label>ORDER</Label><input data-testid="series-order-input" type="number" value={f.order} onChange={(e) => setF({ ...f, order: e.target.value })} className={field} /></label>
      <FormActions busy={busy} onCancel={onCancel} label="SAVE SERIES" />
    </form>
    {initial?.id && (
      <div className="border border-[#F5A524]/30">
        <p className="font-display font-black uppercase text-xl px-3 pt-3">Episodes &amp; panels</p>
        <Episodes series={initial} onChanged={onChanged} />
      </div>
    )}
    </div>
  );
};

const EpisodeForm = ({ initial, onSave, onCancel }) => {
  const [f, setF] = useState({ number: 1, title: "", pages: [], ...initial });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.pages.length) return toast.error("Upload at least one panel image");
    setBusy(true);
    await onSave({ number: Number(f.number) || 1, title: f.title, pages: f.pages });
    setBusy(false);
  };
  return (
    <form onSubmit={submit} data-testid="episode-form" className="space-y-5">
      <div className="grid grid-cols-[120px_1fr] gap-4">
        <label className="block"><Label>EPISODE #</Label><input data-testid="episode-number-input" type="number" min={1} value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} className={field} /></label>
        <label className="block"><Label>TITLE</Label><input data-testid="episode-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={field} /></label>
      </div>
      <PagesField pages={f.pages} onChange={(pages) => setF((s) => ({ ...s, pages }))} />
      <FormActions busy={busy} onCancel={onCancel} label="SAVE EPISODE" />
    </form>
  );
};

const Episodes = ({ series, onChanged }) => {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const load = useCallback(() => api.get(`/webtoons/${series.id}`).then((r) => setData(r.data?.episodes ?? [])).catch(() => setData([])), [series.id]);
  useEffect(() => {
    load();
  }, [load]);
  if (data === null) return <p className="font-mono text-xs text-zinc-400 p-3">LOADING EPISODES...</p>;
  const save = async (body) => {
    try {
      if (editing?.id) await api.put(`/episodes/${editing.id}`, body);
      else await api.post(`/webtoons/${series.id}/episodes`, body);
      toast.success("EPISODE SAVED");
      setEditing(null);
      load();
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const remove = async () => {
    await api.delete(`/episodes/${deleting.id}`).catch((e) => toast.error(formatApiError(e)));
    setDeleting(null);
    load();
    onChanged();
  };
  const nextNum = data.reduce((m, e) => Math.max(m, e.number), 0) + 1;
  return (
    <div className="border-t border-[#F5A524]/15 p-3 space-y-2" data-testid={`episodes-${series.id}`}>
      {data.map((e) => (
        <div key={e.id} data-testid={`episode-row-${e.id}`} className="flex items-center gap-3 bg-[#0B0C10] p-2">
          {e.pages?.[0] && <img src={asset(e.pages[0])} alt="" className="w-10 h-10 object-cover object-top" />}
          <span className="font-mono text-xs text-[#F5A524]">EP {String(e.number).padStart(2, "0")}</span>
          <span className="flex-1 min-w-0 truncate text-sm">{e.title || "Untitled"} <span className="text-zinc-400">· {e.pages?.length ?? 0} panels</span></span>
          <button data-testid={`episode-edit-${e.id}`} onClick={() => setEditing(e)} className="p-2 text-zinc-300 hover:text-[#F5A524]" aria-label="Edit episode"><Pencil size={14} /></button>
          <button data-testid={`episode-delete-${e.id}`} onClick={() => setDeleting(e)} className="p-2 text-zinc-300 hover:text-[#FF2A55]" aria-label="Delete episode"><Trash2 size={14} /></button>
        </div>
      ))}
      <button data-testid={`episode-add-${series.id}`} onClick={() => setEditing({ number: nextNum })}
        className="flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] text-[#F5A524] border border-dashed border-[#F5A524]/40 px-4 py-2.5 hover:bg-[#F5A524]/10">
        <Plus size={14} /> ADD EPISODE {String(nextNum).padStart(2, "0")}
      </button>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-lenis-prevent className="bg-[#12131A] border-[#F5A524]/40 max-w-2xl max-h-[90vh] overflow-y-auto rounded-none">
          <DialogHeader><DialogTitle className="font-display font-black uppercase text-3xl">{editing?.id ? "Edit" : "New"} Episode</DialogTitle></DialogHeader>
          {editing && <EpisodeForm initial={editing} onSave={save} onCancel={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
      <ConfirmDelete item={deleting} label={`Episode ${deleting?.number}`} onCancel={() => setDeleting(null)} onConfirm={remove} />
    </div>
  );
};

export const WebtoonsAdmin = () => {
  const { items, loading, reload } = useList("/webtoons");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [open, setOpen] = useState(null);

  const save = async (body, pages = []) => {
    try {
      if (editing?.id) await api.put(`/webtoons/${editing.id}`, body);
      else {
        const { data } = await api.post("/webtoons", body);
        if (pages.length) await api.post(`/webtoons/${data.id}/episodes`, { number: 1, title: "", pages });
      }
      toast.success("SERIES SAVED");
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const remove = async () => {
    await api.delete(`/webtoons/${deleting.id}`).catch((e) => toast.error(formatApiError(e)));
    setDeleting(null);
    reload();
  };

  return (
    <section className="px-5 sm:px-10 py-10 max-w-5xl" data-testid="admin-webtoons">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
        <div>
          <p className="font-mono text-xs tracking-[0.35em] text-[#F5A524]">// WEBTOON SERIES</p>
          <h1 className="font-display font-black uppercase text-5xl sm:text-6xl">Webtoons <span className="text-[#F5A524]">{items.length}</span></h1>
        </div>
        <button data-testid="admin-webtoons-new-btn" onClick={() => setEditing({})} className="clip-tab flex items-center gap-2 bg-[#F5A524] text-black font-mono font-bold text-sm tracking-[0.2em] px-6 py-3 hover:bg-white transition-colors">
          <Plus size={16} /> NEW SERIES
        </button>
      </div>
      {loading ? <p className="font-mono text-zinc-400">LOADING...</p> : (
        <div className="space-y-3">
          {items.map((s) => (
            <div key={s.id} data-testid={`admin-series-${s.id}`} className="bg-[#12131A] border border-[#F5A524]/15">
              <div className="flex items-center gap-4 p-3">
                <img src={asset(s.cover_url)} alt="" className="w-14 h-16 object-cover object-top shrink-0" />
                <button onClick={() => setOpen(open === s.id ? null : s.id)} data-testid={`admin-series-toggle-${s.id}`} className="flex-1 min-w-0 text-left">
                  <p className="font-display font-bold uppercase text-xl truncate">{s.title}</p>
                  <p className="font-mono text-[11px] tracking-[0.2em] text-[#F5A524] flex items-center gap-1">{s.episode_count} EPISODES · MANAGE PANELS {open === s.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}</p>
                </button>
                <Link to={`/webtoon/${s.id}`} data-testid={`admin-series-view-${s.id}`} className="p-2 text-zinc-400 hover:text-[#F5A524]" aria-label="View"><ExternalLink size={16} /></Link>
                <button data-testid={`admin-series-edit-${s.id}`} onClick={() => setEditing(s)} className="p-2 text-zinc-300 hover:text-[#F5A524]" aria-label="Edit"><Pencil size={16} /></button>
                <button data-testid={`admin-series-delete-${s.id}`} onClick={() => setDeleting(s)} className="p-2 text-zinc-300 hover:text-[#FF2A55]" aria-label="Delete"><Trash2 size={16} /></button>
              </div>
              {open === s.id && <Episodes series={s} onChanged={reload} />}
            </div>
          ))}
          {items.length === 0 && <p className="font-mono text-zinc-400 border border-dashed border-[#F5A524]/30 p-10">NO SERIES YET. THIS SECTION STAYS HIDDEN ON YOUR SITE UNTIL YOU ADD ONE.</p>}
        </div>
      )}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-lenis-prevent className="bg-[#12131A] border-[#F5A524]/40 max-w-2xl max-h-[90vh] overflow-y-auto rounded-none">
          <DialogHeader><DialogTitle className="font-display font-black uppercase text-3xl">{editing?.id ? "Edit" : "New"} Series</DialogTitle></DialogHeader>
          {editing && <SeriesForm initial={editing} onSave={save} onCancel={() => setEditing(null)} onChanged={reload} />}
        </DialogContent>
      </Dialog>
      <ConfirmDelete item={deleting} label={deleting?.title} onCancel={() => setDeleting(null)} onConfirm={remove} />
    </section>
  );
};
